import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const ENTER = [
  { opacity: 0, transform: 'translate3d(0, 10px, 0)' },
  { opacity: 1, transform: 'none' },
];
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
// Per-block stagger: short gap between siblings, capped so a page with many
// blocks still finishes its entrance quickly (Emil: don't make people wait).
const STAGGER_MS = 55;
const STAGGER_CAP_MS = 330;
// Above this many top-level blocks, animate the page as one (a long list at the
// page root shouldn't ripple item-by-item).
const MAX_STAGGER_BLOCKS = 14;

/**
 * Animates the page in on every route change — a staggered fade + lift of the
 * page's top-level sections, matching the dashboard's feel across the whole
 * app. Wired once at AppLayout's content wrapper, so every page gets it for
 * free with no per-page edits.
 *
 * Non-destructive: it never remounts the page (react-query cache, form state,
 * and scroll restoration are untouched) — it uses the Web Animations API to
 * replay the entrance on each navigation. The blocks it staggers are the
 * children of the page's root container (the usual `<div className="flex
 * flex-col gap-…">` wrapper); it falls back to the host's own children when a
 * page returns a fragment of siblings, and to a single whole-page fade when
 * the structure is flat or unusually deep.
 *
 * Emil's framework: transform + opacity only, strong ease-out, <350ms; honours
 * prefers-reduced-motion and degrades to no motion where the API is missing.
 */
export function RouteTransition({ children }: { children: ReactNode }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const host = ref.current;
    if (!host || typeof host.animate !== 'function') return undefined;
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    // Pick the elements to stagger. The page renders inside `host`; usually as
    // a single root container whose children are the sections to cascade. If
    // the page root is a fragment, `host` already holds the siblings directly.
    let blocks = Array.from(host.children) as HTMLElement[];
    if (blocks.length === 1 && blocks[0]!.children.length > 1) {
      blocks = Array.from(blocks[0]!.children) as HTMLElement[];
    }
    // Too few or too many to cascade meaningfully → animate the page as one.
    if (blocks.length <= 1 || blocks.length > MAX_STAGGER_BLOCKS) {
      blocks = [host];
    }

    const anims = blocks.map((node, i) =>
      node.animate(ENTER, {
        duration: 300,
        delay: blocks.length === 1 ? 0 : Math.min(i * STAGGER_MS, STAGGER_CAP_MS),
        easing: EASE,
        // `backwards` holds the first keyframe (invisible) through the delay so
        // staggered blocks don't flash in before their turn.
        fill: 'backwards',
      }),
    );
    return () => anims.forEach((a) => a.cancel());
  }, [pathname]);

  return <div ref={ref}>{children}</div>;
}
